import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Line, ComposedChart } from "recharts";
import { format, parseISO, endOfMonth } from "date-fns";
import { salesProjectionService, ProjectionFilters } from "@/services/salesProjectionService";
import { supabase } from "@/integrations/supabase/client";
import { useDateRange } from "./DateRangeContext";
interface ExploitationDetail {
  client_id: string;
  client_name: string;
  amount: number;
  probability: number;
}
const ProjectionSkeleton = () => <div className="space-y-3">
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-[300px] w-full" />
    <div className="flex gap-4">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-4 w-24" />
    </div>
  </div>;
export function SalesProjectionChart() {
  console.log('🚀 SalesProjectionChart: Component initializing...');
  const {
    dateRange,
    selectedYear,
    setSelectedYear
  } = useDateRange();
  const currentYear = new Date().getFullYear();
  const [filters, setFilters] = useState<ProjectionFilters>({});
  const {
    data: projections,
    isLoading: projectionsLoading,
    error: projectionsError
  } = useQuery({
    // Bump key to avoid stale cached data when projection logic changes
    queryKey: ['sales-projections-with-details-v3', filters, selectedYear],
    queryFn: async () => {
      console.log('🔄 SalesProjectionChart: Starting query...');
      try {
        const result = await salesProjectionService.getMonthlyProjectionsWithDetails(filters);
        console.log('✅ SalesProjectionChart: Query successful, got', result?.length || 0, 'months of data');
        return result || [];
      } catch (error) {
        console.error('❌ SalesProjectionChart: Query failed:', error);
        throw error;
      }
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 10 * 1000,
    // keep chart fresh while user is editing data
    enabled: true,
    retry: 2
  });

  // Query for previous year's sales data (full year for comparison line in chart)
  const {
    data: previousYearSalesFull
  } = useQuery({
    queryKey: ['previous-year-sales-full', selectedYear],
    queryFn: async () => {
      try {
        const previousYear = selectedYear - 1;
        const {
          data,
          error
        } = await supabase.from('invoices').select('amount, issue_date').not('issue_date', 'is', null).gte('issue_date', `${previousYear}-01-01`).lte('issue_date', `${previousYear}-12-31`);
        if (error) throw error;

        // Group by month
        const monthlyData: Record<string, number> = {};
        data?.forEach(invoice => {
          if (invoice.issue_date) {
            const monthKey = format(parseISO(invoice.issue_date), 'yyyy-MM');
            monthlyData[monthKey] = (monthlyData[monthKey] || 0) + (invoice.amount || 0);
          }
        });
        return monthlyData;
      } catch (error) {
        console.error('Error fetching previous year sales:', error);
        return {};
      }
    },
    staleTime: 5 * 60 * 1000
  });

  // Query for 2 years ago sales data (for secondary comparison line)
  const {
    data: twoYearsAgoSalesFull
  } = useQuery({
    queryKey: ['two-years-ago-sales-full', selectedYear],
    queryFn: async () => {
      try {
        const twoYearsAgo = selectedYear - 2;
        const {
          data,
          error
        } = await supabase.from('invoices').select('amount, issue_date').not('issue_date', 'is', null).gte('issue_date', `${twoYearsAgo}-01-01`).lte('issue_date', `${twoYearsAgo}-12-31`);
        if (error) throw error;

        // Group by month
        const monthlyData: Record<string, number> = {};
        data?.forEach(invoice => {
          if (invoice.issue_date) {
            const monthKey = format(parseISO(invoice.issue_date), 'yyyy-MM');
            monthlyData[monthKey] = (monthlyData[monthKey] || 0) + (invoice.amount || 0);
          }
        });
        return monthlyData;
      } catch (error) {
        console.error('Error fetching 2 years ago sales:', error);
        return {};
      }
    },
    staleTime: 5 * 60 * 1000
  });

  // Query for previous year's sales (full months for trend comparison)
  const {
    data: previousYearSalesYTD
  } = useQuery({
    queryKey: ['previous-year-sales-ytd', selectedYear, dateRange.from, dateRange.to],
    queryFn: async () => {
      try {
        // Always compare with full months of the previous year
        const previousYearFromMonth = dateRange.from.getMonth();
        const previousYearToMonth = dateRange.to.getMonth();
        const previousPeriodStart = new Date(dateRange.from.getFullYear() - 1, previousYearFromMonth, 1);
        const previousPeriodEnd = endOfMonth(new Date(dateRange.to.getFullYear() - 1, previousYearToMonth, 1));
        const {
          data,
          error
        } = await supabase.from('invoices').select('amount, issue_date').not('issue_date', 'is', null).gte('issue_date', format(previousPeriodStart, 'yyyy-MM-dd')).lte('issue_date', format(previousPeriodEnd, 'yyyy-MM-dd'));
        if (error) throw error;

        // Group by month
        const monthlyData: Record<string, number> = {};
        data?.forEach(invoice => {
          if (invoice.issue_date) {
            const monthKey = format(parseISO(invoice.issue_date), 'yyyy-MM');
            monthlyData[monthKey] = (monthlyData[monthKey] || 0) + (invoice.amount || 0);
          }
        });
        return monthlyData;
      } catch (error) {
        console.error('Error fetching previous year YTD sales:', error);
        return {};
      }
    },
    staleTime: 5 * 60 * 1000
  });

  // Query for exploitation data
  const {
    data: exploitationData
  } = useQuery({
    queryKey: ['exploitation-data-for-chart', selectedYear],
    queryFn: async () => {
      try {
        // Fetch exploitations with client names for the selected year
        const {
          data: exploitations,
          error
        } = await supabase.from('client_exploitations').select(`
            id,
            client_id,
            month,
            sales,
            probability,
            year,
            clients!client_exploitations_client_id_fkey (
              id,
              name
            )
          `).eq('year', selectedYear).eq('status', 'Included');
        if (error) throw error;

        // Group by month and calculate weighted amounts
        const monthlyExploitation: Record<number, {
          total: number;
          details: ExploitationDetail[];
        }> = {};
        exploitations?.forEach(exp => {
          if (exp.month && exp.sales) {
            const estimatedSales = (exp.sales || 0) * ((exp.probability || 0) / 100);
            const clientName = (exp.clients as {
              id: string;
              name: string;
            } | null)?.name || 'Unknown Client';
            if (!monthlyExploitation[exp.month]) {
              monthlyExploitation[exp.month] = {
                total: 0,
                details: []
              };
            }
            monthlyExploitation[exp.month].total += estimatedSales;
            monthlyExploitation[exp.month].details.push({
              client_id: exp.client_id,
              client_name: clientName,
              amount: estimatedSales,
              // Use weighted value (sales × probability)
              probability: exp.probability || 0
            });
          }
        });
        return monthlyExploitation;
      } catch (error) {
        console.error('Error fetching exploitation data:', error);
        return {};
      }
    },
    staleTime: 5 * 60 * 1000
  });
  console.log('🎯 Component State:', {
    projectionsLoading,
    projectionsError: !!projectionsError,
    projectionsLength: projections?.length || 0,
    selectedYear
  });
  if (projectionsLoading) {
    return <Card>
        <CardHeader>
          <CardTitle>Sales Projection</CardTitle>
          <CardDescription>Revenue forecast with confirmed and weighted pipeline</CardDescription>
        </CardHeader>
        <CardContent>
          <ProjectionSkeleton />
        </CardContent>
      </Card>;
  }
  if (projectionsError) {
    console.error('🚨 ProjectionsError:', projectionsError);
    return <Card>
        <CardHeader>
          <CardTitle>Sales Projection</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertDescription>
              Error loading sales projections: {(projectionsError as Error).message}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>;
  }

  // Safely filter projections for selected year only
  let yearFilteredProjections = [];
  try {
    yearFilteredProjections = projections?.filter(p => p?.month?.startsWith(selectedYear.toString())) || [];
    console.log('🎯 Year filtered projections:', yearFilteredProjections.length);
  } catch (error) {
    console.error('🚨 Error filtering projections:', error);
    yearFilteredProjections = [];
  }

  // Create full year data with all 12 months - with comprehensive error handling
  let chartData = [];
  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    chartData = monthNames.map((monthName, index) => {
      try {
        const monthNumber = (index + 1).toString().padStart(2, '0');
        const monthIndex = index + 1; // 1-12
        const fullMonth = `${selectedYear}-${monthNumber}`;
        const previousYearMonth = `${selectedYear - 1}-${monthNumber}`;

        // Find data for this month if it exists
        const monthData = yearFilteredProjections?.find(p => p?.month === fullMonth);

        // Get previous year sales for this month (full year data for chart line)
        const previousYearSalesForMonth = previousYearSalesFull?.[previousYearMonth] || 0;

        // Get 2 years ago sales for this month
        const twoYearsAgoMonth = `${selectedYear - 2}-${monthNumber}`;
        const twoYearsAgoSalesForMonth = twoYearsAgoSalesFull?.[twoYearsAgoMonth] || 0;

        // Get exploitation data for this month
        const exploitationForMonth = exploitationData?.[monthIndex] || {
          total: 0,
          details: []
        };
        return {
          month: monthName,
          fullMonth: fullMonth,
          actuals: Number(monthData?.actuals || 0),
          confirmed: Number(monthData?.confirmed_pipeline || 0),
          weighted: Number(monthData?.weighted_pipeline || 0),
          exploitation: Number(exploitationForMonth.total || 0),
          exploitationDetails: exploitationForMonth.details || [],
          total: Number(monthData?.total_projection || 0) + Number(exploitationForMonth.total || 0),
          previousYearSales: Number(previousYearSalesForMonth),
          twoYearsAgoSales: Number(twoYearsAgoSalesForMonth),
          details: monthData?.details || []
        };
      } catch (error) {
        console.error(`🚨 Error processing month ${monthName}:`, error);
        return {
          month: monthName,
          fullMonth: `${selectedYear}-${(index + 1).toString().padStart(2, '0')}`,
          actuals: 0,
          confirmed: 0,
          weighted: 0,
          exploitation: 0,
          exploitationDetails: [],
          total: 0,
          previousYearSales: 0,
          twoYearsAgoSales: 0,
          details: []
        };
      }
    });
    console.log('✅ Chart data created successfully:', chartData.length, 'months');
  } catch (error) {
    console.error('🚨 Critical error creating chart data:', error);
    // Return empty chart data as fallback
    chartData = [];
  }
  const formatCurrency = (value: number) => {
    try {
      return new Intl.NumberFormat('el-GR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value || 0);
    } catch (error) {
      return `€${value || 0}`;
    }
  };
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      const monthData = chartData.find(d => d.month === label);
      console.log('🎯 Tooltip Debug:', {
        label,
        monthData: monthData,
        details: monthData?.details,
        detailsLength: monthData?.details?.length
      });
      if (!monthData) {
        return null;
      }

      // Group details by category
      const detailsByCategory = monthData.details.reduce((acc, detail) => {
        if (!acc[detail.category]) {
          acc[detail.category] = [];
        }
        acc[detail.category].push(detail);
        return acc;
      }, {} as Record<string, typeof monthData.details>);
      console.log('🎯 Details by category:', detailsByCategory);

      // Calculate totals for YoY comparison (includes all Actual & Forecasted Sales)
      const currentMonthTotal = monthData.actuals + monthData.confirmed + monthData.weighted + monthData.exploitation;
      const previousYearSame = monthData.previousYearSales;
      const yoyGrowth = previousYearSame > 0 ? (currentMonthTotal - previousYearSame) / previousYearSame * 100 : 0;

      // Calculate YTD values
      const currentMonthIndex = chartData.findIndex(d => d.month === label);
      const ytdCurrent = chartData.slice(0, currentMonthIndex + 1).reduce((sum, m) => sum + (m.total || 0), 0);
      const ytdPrevious = chartData.slice(0, currentMonthIndex + 1).reduce((sum, m) => sum + m.previousYearSales, 0);
      const ytdGrowth = ytdPrevious > 0 ? (ytdCurrent - ytdPrevious) / ytdPrevious * 100 : 0;
      return <div className="bg-background border border-border rounded-lg p-4 shadow-lg min-w-[300px]">
          <div className="flex items-center justify-between mb-3">
            <p className="font-semibold text-lg">{label} {selectedYear}</p>
            {format(new Date(), 'MMM') === label && <span className="text-red-500 text-sm font-medium">Today</span>}
          </div>


          {/* Client breakdown if available */}
          {monthData.details && monthData.details.length > 0 && <div className="space-y-3">
              {/* Invoiced section */}
              {detailsByCategory.actuals && detailsByCategory.actuals.length > 0 && <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#2563eb'
              }} />
                    <span className="font-medium text-blue-600">
                      Invoiced: ({formatCurrency(detailsByCategory.actuals.reduce((sum, d) => sum + d.amount, 0))})
                    </span>
                  </div>
                  <div className="ml-5 space-y-1">
                    {detailsByCategory.actuals.map((detail, idx) => <div key={idx} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{detail.client_name}:</span>
                        <span className="font-medium">{formatCurrency(detail.amount)}</span>
                      </div>)}
                  </div>
                </div>}

              {/* To be Invoiced section */}
              {detailsByCategory.confirmed && detailsByCategory.confirmed.length > 0 && <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#10b981'
              }} />
                    <span className="font-medium text-green-600">
                      To be Invoiced: ({formatCurrency(detailsByCategory.confirmed.reduce((sum, d) => sum + d.amount, 0))})
                    </span>
                  </div>
                  <div className="ml-5 space-y-1">
                    {detailsByCategory.confirmed.map((detail, idx) => <div key={idx} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{detail.client_name}:</span>
                        <div className="text-right">
                          <span className="font-medium">{formatCurrency(detail.amount)}</span>
                          {detail.probability && detail.probability < 100 && <span className="text-xs text-muted-foreground ml-2">({detail.probability}%)</span>}
                        </div>
                      </div>)}
                  </div>
                </div>}

              {/* Weighted Pipeline section */}
              {detailsByCategory.weighted && detailsByCategory.weighted.length > 0 && <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#f59e0b'
              }} />
                    <span className="font-medium text-orange-600">
                      Weighted Pipeline: ({formatCurrency(detailsByCategory.weighted.reduce((sum, d) => sum + d.amount, 0))})
                    </span>
                  </div>
                  <div className="ml-5 space-y-1">
                    {detailsByCategory.weighted.map((detail, idx) => <div key={idx} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{detail.client_name}:</span>
                        <div className="text-right">
                          <span className="font-medium">{formatCurrency(detail.amount)}</span>
                          {detail.probability && <span className="text-xs text-muted-foreground ml-2">({detail.probability}%)</span>}
                        </div>
                      </div>)}
                  </div>
                </div>}

              {/* Exploitation section */}
              {monthData.exploitationDetails && monthData.exploitationDetails.length > 0 && <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#ec4899'
              }} />
                    <span className="font-medium text-pink-600">
                      Exploitation: ({formatCurrency(monthData.exploitationDetails.reduce((sum: number, d: ExploitationDetail) => sum + d.amount, 0))})
                    </span>
                  </div>
                  <div className="ml-5 space-y-1">
                    {monthData.exploitationDetails.map((detail: ExploitationDetail, idx: number) => <div key={idx} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{detail.client_name}:</span>
                        <div className="text-right">
                          <span className="font-medium">{formatCurrency(detail.amount)}</span>
                          <span className="text-xs text-muted-foreground ml-2">({detail.probability}%)</span>
                        </div>
                      </div>)}
                  </div>
                </div>}
            </div>}

          {/* Summary totals */}
          <div className="border-t border-border pt-3 mt-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="font-semibold">Total: {formatCurrency(currentMonthTotal)}</span>
              <span className={`text-sm font-medium ${yoyGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {yoyGrowth >= 0 ? '+' : ''}{yoyGrowth.toFixed(0)}% vs YA
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="font-semibold">YTD: {formatCurrency(ytdCurrent)}</span>
              <span className={`text-sm font-medium ${ytdGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {ytdGrowth >= 0 ? '+' : ''}{ytdGrowth.toFixed(0)}% vs YA
              </span>
            </div>
          </div>

        </div>;
    }
    return null;
  };

  // Calculate summary statistics safely
  let summaryStats = {
    currentYearTotal: 0,
    currentYearActuals: 0,
    currentYearConfirmed: 0,
    currentYearWeighted: 0,
    currentYearExploitation: 0,
    averageProbability: 0,
    averageExploitationProbability: 0,
    previousYearActuals: 0,
    previousYearTotal: 0,
    actualsGrowth: 0,
    totalGrowth: 0
  };
  try {
    const allWeightedItems = projections?.flatMap(month => month.details?.filter(detail => detail.category === 'weighted') || []) || [];
    const totalProbability = allWeightedItems.reduce((sum, item) => sum + (item.probability || 0), 0);
    const averageProbability = allWeightedItems.length > 0 ? totalProbability / allWeightedItems.length : 0;

    // Calculate average exploitation probability
    const allExploitationItems = Object.values(exploitationData || {}).flatMap(month => month.details || []);
    const totalExploitationProbability = allExploitationItems.reduce((sum, item) => sum + (item.probability || 0), 0);
    const averageExploitationProbability = allExploitationItems.length > 0 ? totalExploitationProbability / allExploitationItems.length : 0;

    // Total Projection is sum of ALL 12 months (full year projection)
    const currentYearTotal = chartData.reduce((sum, month) => sum + (month.total || 0), 0);
    const currentYearConfirmed = chartData.reduce((sum, month) => sum + (month.confirmed || 0), 0);
    const currentYearWeighted = chartData.reduce((sum, month) => sum + (month.weighted || 0), 0);
    const currentYearExploitation = chartData.reduce((sum, month) => sum + (month.exploitation || 0), 0);

    // Previous year TOTAL (all 12 months) for Total Projection trend
    const previousYearTotal = chartData.reduce((sum, month) => sum + (month.previousYearSales || 0), 0);

    // Total Projection growth: compare full year projection vs full year previous year actuals
    const totalGrowth = previousYearTotal > 0 ? (currentYearTotal - previousYearTotal) / previousYearTotal * 100 : 0;

    // For Invoiced: YTD only makes sense for current year
    // For past years, show full year data
    const isCurrentYear = selectedYear === currentYear;
    const toDate = dateRange.to;
    const toMonth = toDate.getMonth(); // 0-indexed

    // If viewing current year: use YTD (up to current month)
    // If viewing past year: use full year (all 12 months)
    const monthsToInclude = isCurrentYear ? toMonth + 1 : 12;
    const currentYearActuals = chartData.slice(0, monthsToInclude).reduce((sum, month) => sum + (month.actuals || 0), 0);

    // For comparison: if current year, compare YTD vs same period last year
    // If past year, compare full year vs full previous year
    let previousYearActuals = 0;
    let actualsGrowth = 0;
    
    if (isCurrentYear) {
      // YTD comparison with same period last year
      previousYearActuals = Object.values(previousYearSalesYTD || {}).reduce((sum, amount) => sum + amount, 0);
      actualsGrowth = previousYearActuals > 0 ? (currentYearActuals - previousYearActuals) / previousYearActuals * 100 : 0;
    } else {
      // Full year comparison with previous full year
      previousYearActuals = previousYearTotal; // This is already full year
      actualsGrowth = previousYearActuals > 0 ? (currentYearActuals - previousYearActuals) / previousYearActuals * 100 : 0;
    }
    
    summaryStats = {
      currentYearTotal,
      currentYearActuals,
      currentYearConfirmed,
      currentYearWeighted,
      currentYearExploitation,
      averageProbability: Math.round(averageProbability),
      averageExploitationProbability: Math.round(averageExploitationProbability),
      previousYearActuals,
      previousYearTotal,
      actualsGrowth,
      totalGrowth
    };
  } catch (error) {
    console.error('🚨 Error calculating summary stats:', error);
  }
  return <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <CardTitle>Sales Projection</CardTitle>
            <CardDescription>
          </CardDescription>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Select value={selectedYear.toString()} onValueChange={value => setSelectedYear(parseInt(value))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({
                length: 3
              }, (_, i) => currentYear + i - 1).map(year => <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-4">
          <div className="bg-card rounded-lg p-3 border">
            <div className="text-sm font-medium text-muted-foreground">Invoiced</div>
            <div className="text-lg font-semibold text-blue-600">{formatCurrency(summaryStats.currentYearActuals)}</div>
            <div className={`text-xs font-medium mt-1 ${summaryStats.actualsGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {summaryStats.actualsGrowth >= 0 ? '+' : ''}{summaryStats.actualsGrowth.toFixed(1)}% vs YA
            </div>
          </div>
          <div className="bg-card rounded-lg p-3 border">
            <div className="text-sm font-medium text-muted-foreground">To be Invoiced</div>
            <div className="text-lg font-semibold text-green-600">{formatCurrency(summaryStats.currentYearConfirmed)}</div>
          </div>
          <div className="bg-card rounded-lg p-3 border">
            <div className="text-sm font-medium text-muted-foreground">Proposals</div>
            <div className="text-lg font-semibold text-orange-600">
              {formatCurrency(summaryStats.currentYearWeighted)}
              {summaryStats.averageProbability > 0 && <span className="text-sm font-normal text-muted-foreground ml-1">
                  ({summaryStats.averageProbability}%)
                </span>}
            </div>
          </div>
          <div className="bg-card rounded-lg p-3 border">
            <div className="text-sm font-medium text-muted-foreground">Exploitation</div>
            <div className="text-lg font-semibold text-pink-600">
              {formatCurrency(summaryStats.currentYearExploitation)}
              {summaryStats.averageExploitationProbability > 0 && <span className="text-sm font-normal text-muted-foreground ml-1">
                  ({summaryStats.averageExploitationProbability}%)
                </span>}
            </div>
          </div>
          <div className="bg-card rounded-lg p-3 border">
            <div className="text-sm font-medium text-muted-foreground">Total Projection</div>
            <div className="text-lg font-semibold text-purple-600">{formatCurrency(summaryStats.currentYearTotal)}</div>
            <div className={`text-xs font-medium mt-1 ${summaryStats.totalGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {summaryStats.totalGrowth >= 0 ? '+' : ''}{summaryStats.totalGrowth.toFixed(1)}% vs YA
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 ? <div className="h-[400px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="month" tick={{
              fontSize: 12
            }} />
                <YAxis tick={{
              fontSize: 12
            }} tickFormatter={value => `€${(value / 1000).toFixed(0)}k`} />
                <Tooltip content={CustomTooltip} />
                <Legend />
                
              {/* Actuals - Blue area */}
              <Area type="monotone" dataKey="actuals" stackId="1" stroke="#2563eb" fill="#2563eb" fillOpacity={0.8} name="Actuals (Invoiced)" />
              
              {/* Confirmed Pipeline - Green area */}
              <Area type="monotone" dataKey="confirmed" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.7} name="Confirmed Pipeline" />
              
              {/* Weighted Pipeline - Orange area */}
              <Area type="monotone" dataKey="weighted" stackId="1" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.6} name="Weighted Pipeline" />
              
              {/* Exploitation - Pink area */}
              <Area type="monotone" dataKey="exploitation" stackId="1" stroke="#ec4899" fill="#ec4899" fillOpacity={0.5} name="Exploitation" />
              
              {/* Current month reference line */}
              <ReferenceLine x={format(new Date(), 'MMM')} stroke="#ef4444" strokeWidth={2} strokeDasharray="4 4" label={{
              value: "Today",
              position: "top",
              fill: "#ef4444"
            }} />
              
              {/* Previous year sales line */}
              <Line type="monotone" dataKey="previousYearSales" stroke="#ef4444" strokeWidth={2} strokeDasharray="8 4" dot={false} name={`${selectedYear - 1} Sales`} />
              
              {/* 2 years ago sales line - dark gray dashed */}
              <Line type="monotone" dataKey="twoYearsAgoSales" stroke="#4b5563" strokeWidth={2} strokeDasharray="8 4" dot={false} name={`${selectedYear - 2} Sales`} />
              </ComposedChart>
            </ResponsiveContainer>
          </div> : <div className="h-[400px] flex items-center justify-center">
            <p className="text-muted-foreground">No data available for the selected year.</p>
          </div>}
      </CardContent>
    </Card>;
}